import {
  type Component,
  createMemo,
  createSignal,
  For,
  onCleanup,
  Show,
} from 'solid-js';
import tinycolor from 'tinycolor2';
import { colorsAreEqual } from '@trimble/utilities';
import { defaultColorsFlat } from './default-color-palette';
import { getClosestNamedColor } from './color-utils';
import type { CompactColorPickerProps } from './CompactColorPicker.types';

export type {
  CompactColorPickerAlign,
  CompactColorPickerProps,
} from './CompactColorPicker.types';

/** Fixed swatch size so the grid never collapses columns below the circles. */
const SWATCH_SIZE_CLASS = 'w-5 h-5';
const PALETTE_COLUMNS = 6;
const PALETTE_GAP_PX = 10;
const SWATCH_PX = 20;
const PALETTE_PADDING_PX = 12;
const PALETTE_MIN_WIDTH_PX =
  PALETTE_COLUMNS * SWATCH_PX +
  (PALETTE_COLUMNS - 1) * PALETTE_GAP_PX +
  PALETTE_PADDING_PX;

/**
 * Text-free color picker: a circular swatch trigger and a palette popover.
 * Intended for dense rows where the full {@link ColorPicker} is too wide.
 */
export const CompactColorPicker: Component<CompactColorPickerProps> = (
  props
) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const paletteVisibility = {
    isOpen,
    toggle: (): void => {
      setIsOpen((open) => !open);
    },
    dismiss: (): void => {
      setIsOpen(false);
    },
    select: (hex: string): void => {
      props.onChange(hex);
      setIsOpen(false);
    },
  };
  let root: HTMLDivElement | undefined;

  const testId = (): string => props['data-testid'] ?? 'compact-color-picker';
  const currentHex = (): string => tinycolor(props.color).toHexString();

  const palette = createMemo(() => {
    const current = currentHex();
    const isInPalette = defaultColorsFlat.some((color) =>
      colorsAreEqual(color, current)
    );
    return isInPalette ? defaultColorsFlat : [current, ...defaultColorsFlat];
  });

  const handleDocumentPointerDown = (event: PointerEvent): void => {
    const target = event.target;
    if (root && (!(target instanceof Node) || !root.contains(target))) {
      paletteVisibility.dismiss();
    }
  };
  const handleDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      paletteVisibility.dismiss();
    }
  };
  document.addEventListener('pointerdown', handleDocumentPointerDown);
  document.addEventListener('keydown', handleDocumentKeyDown);
  onCleanup(() => {
    document.removeEventListener('pointerdown', handleDocumentPointerDown);
    document.removeEventListener('keydown', handleDocumentKeyDown);
  });

  return (
    <div ref={root} class="relative inline-flex" data-testid={testId()}>
      <button
        type="button"
        data-testid={`${testId()}-trigger`}
        class={`${SWATCH_SIZE_CLASS} shrink-0 rounded-full border border-gray-3 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
        style={{ background: currentHex() }}
        title={props.tooltip ?? getClosestNamedColor(currentHex())}
        aria-haspopup="true"
        aria-expanded={paletteVisibility.isOpen()}
        disabled={props.disabled}
        onClick={() => paletteVisibility.toggle()}
      />
      <Show when={paletteVisibility.isOpen()}>
        <div
          data-testid={`${testId()}-palette`}
          data-align={props.align ?? 'right'}
          class={`absolute top-full mt-1 z-dropdown bg-white shadow-md shadow-gray-8 p-1.5 grid gap-2.5 ${
            (props.align ?? 'right') === 'right' ? 'right-0' : 'left-0'
          }`}
          style={{
            'grid-template-columns': `repeat(${PALETTE_COLUMNS}, ${SWATCH_PX}px)`,
            'min-width': `${PALETTE_MIN_WIDTH_PX}px`,
          }}
        >
          <For each={palette()}>
            {(item) => (
              <button
                type="button"
                class={`${SWATCH_SIZE_CLASS} shrink-0 rounded-full cursor-pointer hover:ring-2 hover:ring-gray-2 ${
                  colorsAreEqual(item, currentHex()) ? 'ring-2 ring-gray-6' : ''
                }`}
                style={{ background: item }}
                title={getClosestNamedColor(item)}
                aria-label={getClosestNamedColor(item)}
                onClick={() =>
                  paletteVisibility.select(tinycolor(item).toHexString())
                }
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};

export default CompactColorPicker;
